import {
	formatNumberWithCommas,
	multiplyWithPrecision,
	pxToRem,
	setDataToClipboard,
} from "@/util/common";
import { Popup, Image, Button, Space } from "antd-mobile";
import { createUseStyles } from "react-jss";
import useCustomTranslation from "@/hooks/useTranslation";
import useCommonFormStyles from "@/hooks/useCommonStyles";
import { useUserInfo } from "@/hooks/useUserInfo";
import { useGameNavigation } from "@/hooks/useGameNavigation";

const BettingPopup: React.FC<{
	visible: boolean;
	data: Record<string, any>;
	onClose?: () => void;
}> = (props) => {
	const classes = useStyles();
	const { t } = useCustomTranslation();
	const commonFormStyles = useCommonFormStyles();
	const { nickname } = useUserInfo();
	const { goToPlay } = useGameNavigation();

	const refIdList = props.data?.refId?.split("##");
	const winLossValue = multiplyWithPrecision(
		props.data?.rewardCoin ?? 0,
		props.data?.coin ?? 0,
		4
	);
	return (
		<Popup
			bodyStyle={{
				borderTopLeftRadius: pxToRem(30),
				borderTopRightRadius: pxToRem(30),
			}}
			maskStyle={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}
			bodyClassName={classes.popupContent}
			visible={props.visible}
			closeOnMaskClick
			onClose={props.onClose}
		>
			<header className={classes.header}>
				<h2 className={classes.title}>
					<Image
						src={require("@/assets/images/icons/logo_small.png")}
						width={pxToRem(24)}
					/>
					<div className="name">
						<span>COIN</span>
						<span>GAMING</span>
					</div>
				</h2>
				<strong>{t("Betting")}</strong>
				<div
					onClick={() => {
						props.onClose?.();
					}}
					className="close"
				>
					{t("Close")}
				</div>
			</header>

			<section
				style={{
					flex: 1,
					overflowY: "scroll",
					paddingTop: pxToRem(15),
				}}
			>
				<Space
					direction="vertical"
					block
					className={classes.detailsOrder}
					style={{ "--gap": "0" }}
				>
					<h3>{props.data.refId.split("##")[0]}</h3>
					<div
						onClick={() => {
							setDataToClipboard(
								props.data.transNo,
								t("CopySuccess")
							);
						}}
						className="copy"
					>
						<span>ID {props.data.transNo}</span>
						<Image
							src={require("@/assets/images/icons/copy.png")}
							className="icon"
						/>
					</div>
					<p>
						{t("BettingPlayer")}: {nickname}
					</p>
					<p>
						{t("At")} {props.data.updateTime}
					</p>
				</Space>

				<ul className={classes.list}>
					<li>
						<dl>
							<dt>
								<Image
									src={require("@/assets/images/icons/coins-stacked.png")}
									className="icon"
								/>
								<span>{t("Betting")}</span>
							</dt>
							<dd className="u">
								${formatNumberWithCommas(props.data.coin)}
							</dd>
						</dl>
					</li>
					<li>
						<dl>
							<dt>
								<Image
									src={require("@/assets/images/icons/coins-stacked.png")}
									className="icon"
								/>
								<span>{t("Rebate")}</span>
							</dt>
							<dd className="u">
								$
								{formatNumberWithCommas(
									props.data.rewardCoin ?? 0
								)}
							</dd>
						</dl>
					</li>
					<li>
						<dl>
							<dt>
								<Image
									src={require("@/assets/images/icons/coins-stacked.png")}
									className="icon"
								/>
								<span>{t("WinLoss")}</span>
							</dt>
							<dd className={winLossValue > 0 ? "win" : "lose"}>
								${formatNumberWithCommas(winLossValue)}
							</dd>
						</dl>
					</li>
				</ul>

				{refIdList.length > 1 && (
					<Button
						onClick={() => {
							goToPlay({
								gameId: refIdList[1],
								name: props.data.refId.split("##")[0],
								coverPic: "",
								categoryId: "",
								gameCompany: "",
							});
						}}
						block
						shape="rounded"
						className={commonFormStyles.submit}
					>
						<Space
							style={{
								"--gap": pxToRem(6),
							}}
						>
							<span>
								{t("LeaveFor")}
								{/* {props.data.refId.split("##")[0]} */}
							</span>
						</Space>
					</Button>
				)}
			</section>
		</Popup>
	);
};

export default BettingPopup;

const useStyles = createUseStyles({
	popupContent: {
		display: "flex",
		flexDirection: "column",
		backgroundColor: "#22293F",
		color: "#FFFFFF",
		padding: {
			left: pxToRem(24),
			right: pxToRem(24),
			bottom: pxToRem(42),
		},
		maxHeight: "85vh",
	},
	header: {
		position: "relative",
		padding: {
			top: pxToRem(12),
			bottom: pxToRem(12),
		},
		textAlign: "center",
		"& strong": {
			color: "#FFFFFF",
			fontSize: pxToRem(16),
			fontWeight: 500,
			fontFamily: "Noto Sans SC",
		},
		"& .close": {
			position: "absolute",
			right: pxToRem(-10),
			top: 0,
			lineHeight: pxToRem(50),
			padding: {
				left: pxToRem(10),
				right: pxToRem(10),
			},
		},
	},
	title: {
		position: "absolute",
		left: 0,
		top: pxToRem(12),
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		fontSize: pxToRem(15),
		fontWeight: 600,
		color: "#FFFFFF",
		"& .name": {
			display: "flex",
			flexDirection: "column",
			paddingLeft: pxToRem(4),
			textAlign: "left",
			lineHeight: 0.98,
		},
	},
	detailsOrder: {
		textAlign: "center",
		marginBottom: pxToRem(22),
		"& h3": {
			color: "#FFFFFF",
			fontSize: pxToRem(18),
			fontWeight: 600,
			lineHeight: pxToRem(34),
		},
		"& .copy": {
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			color: "#FFFFFF",
			fontSize: pxToRem(14),
			fontWeight: 400,
			lineHeight: pxToRem(34),
			"& .icon": {
				width: pxToRem(14),
				height: pxToRem(14),
				marginLeft: pxToRem(6),
			},
		},
		"& p": {
			color: "#868FA8",
			fontFamily: "Noto Sans SC",
			fontSize: pxToRem(14),
			fontWeight: 400,
			lineHeight: pxToRem(20),
			marginTop: pxToRem(5),
		},
	},
	list: {
		borderRadius: pxToRem(10),
		overflow: "hidden",
		marginBottom: pxToRem(12),
		"& li": {
			display: "flex",
			alignItems: "center",
			color: "#FFFFFF",
			fontSize: pxToRem(16),
			fontWeight: 500,
			backgroundColor: "#181D2C",
			padding: {
				top: pxToRem(20),
				bottom: pxToRem(20),
				left: pxToRem(20),
				right: pxToRem(8),
			},
			borderBottom: "1px solid #2F3A62",
			"& .icon": {
				width: pxToRem(40),
				height: pxToRem(40),
				marginRight: pxToRem(14),
			},
			"&:active": {
				opacity: 0.8,
			},
			"&:last-of-type": {
				borderBottom: "none",
			},
			"& dt": {
				display: "flex",
				alignItems: "center",
				color: "#868FA8",
				fontSize: pxToRem(12),
				fontWeight: 400,
				"& .icon": {
					width: pxToRem(20),
					height: pxToRem(20),
					marginRight: pxToRem(4),
				},
			},
			"& dd": {
				color: "#FFFFFF",
				fontSize: pxToRem(14),
				fontWeight: 500,
				lineHeight: pxToRem(34),
				"&.u": {
					fontSize: pxToRem(18),
					fontWeight: 700,
				},
			},
		},
	},
});
